import { Share as CapacitorShare } from "@capacitor/share";
import { createFileRoute, useRouteContext, useRouter } from "@tanstack/react-router";
import { useMemo, useState, type JSX } from "react";

import HelpCircle from "@repo/icons/default/HelpCircle";
import Left from "@repo/icons/default/Left";
import Share from "@repo/icons/default/Share";
import StarFill from "@repo/icons/default/StarFill";
import { CpsBadge, CpsButton, CpsToast } from "corpus";

import { FullScreenModalNominate } from "@/components/referral/full-screen-modal-nominate";
import { ReferralStep } from "@/components/referral/step/referral-step";
import { Button } from "@/components/ui/button/button";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { formatCurrencyFromCentsToReais } from "@/lib/helpers/currency";
import { trackEvent } from "@/lib/tracking.ts";

const ReferralPage = (): JSX.Element => {
  const { user, flags } = useRouteContext({ from: "__root__" });
  const router = useRouter();

  const [openCopiedLinkToast, setOpenCopiedLinkToast] = useState(false);
  const [isNominateOpen, setIsNominateOpen] = useState(false);

  const referralValue = useMemo(
    () => flags["valor-do-gentileza-gera-gentileza"],
    [flags],
  );

  const showNewReferralFlow = useMemo(
    () => flags["exibe-novo-fluxo-de-indicado"],
    [flags],
  );

  const formattedReferralValue = useMemo(
    () => formatCurrencyFromCentsToReais(referralValue * 100),
    [referralValue],
  );

  const steps = [
    {
      stepNumber: 1,
      title: "Você indica um colega",
      description: "Envie seu link exclusivo pelo app",
    },
    {
      stepNumber: 2,
      title: "Seu colega assina e vocês dois ganham",
      description: "Colega vira membro da Livance",
    },
    {
      stepNumber: 3,
      title: `Os dois recebem ${formattedReferralValue}`,
      description: "Crédito aplicado automaticamente",
    },
  ];

  const shareLink = async (): Promise<void> => {
    const codUsuario = user.codUsuario;

    trackEvent("Botão Clicado", {
      codUsuario,
      label: "Compartilhar Link",
      page: "/settings/referral/",
    });

    const url = `https://livance.me/conheca-a-livance?utm_content=${codUsuario}`;

    // if browser does not support share API, copy link to clipboard
    const canShare = await CapacitorShare.canShare();
    if (!canShare.value) {
      await navigator.clipboard.writeText(url);
      setOpenCopiedLinkToast(true);
    }

    await CapacitorShare.share({
      title: "Venha para a Livance!",
      text: `Abri meu consultório na Livance e estou curtindo bastante! Dá uma olhada, acho que pode ser uma boa pra você também. Se decidir contratar, usando meu link você ainda ganha ${formattedReferralValue} de crédito:`,
      url,
    });
  };

  const handleClickAbout = (): void => {
    void router.navigate({ to: "/settings/referral/about" });
  };

  const handleNominateClick = (): void => {
    trackEvent("Botão Indicar Via Livance Clicado");
    setIsNominateOpen(true);
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Gentileza Gera Gentileza" align="center" />
        <HeaderButton icon={HelpCircle} onClick={handleClickAbout} align="end" />
      </HeaderRoot>

      <Page className="pt-0 px-0 md:px-0 font-poppins">
        <section
          aria-label="Benefício do programa"
          className="flex flex-col items-center justify-center gap-1 px-4 pt-6 pb-7 md:pt-10 md:pb-10 text-center font-poppins"
        >
          <h2 className="text-lg md:text-2xl font-semibold text-neutral-600 uppercase">
            Você e seu colega ganham
          </h2>
          <span className="text-5xl md:text-7xl font-semibold text-primary-400 mt-1">
            {formattedReferralValue}
          </span>
          <p className="text-sm md:text-base text-neutral-400">
            de crédito na fatura da Livance
          </p>
          <CpsBadge color="primary" className="mt-3 md:text-sm md:h-[26px]">
            <span className="flex flex-row items-center gap-1">
              <StarFill className="fill-primary-400" />
              <p>Milhares de membros já indicaram a Livance</p>
            </span>
          </CpsBadge>
        </section>

        <h3 className="text-left text-xl md:text-2xl text-neutral-600 font-semibold w-full px-4">
          Como funciona?
        </h3>
        <div className="flex flex-col gap-2 px-4">
          <ul className="flex flex-col gap-3 md:gap-5 py-4 text-sm md:text-base font-normal text-neutral-600">
            {steps.map((step) => (
              <ReferralStep
                key={step.title}
                title={step.title}
                description={step.description}
                stepNumber={step.stepNumber}
              />
            ))}
          </ul>

          <div className="pb-[calc(8.5rem+env(safe-area-inset-bottom))] md:pb-6 w-full shrink-0">
            <div className="flex flex-col gap-2 fixed md:relative md:bottom-auto md:left-auto md:right-auto bottom-0 left-0 right-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:pb-4 md:px-0 z-50">
              {showNewReferralFlow ? (
                <>
                  <CpsButton
                    onClick={() => {
                      void shareLink();
                    }}
                    fullWidth
                    color="secondary"
                  >
                    Enviar link ao colega
                  </CpsButton>
                  <CpsButton
                    onClick={handleNominateClick}
                    fullWidth
                    color="secondary"
                    variant="outlined"
                  >
                    Indicar via Livance
                  </CpsButton>
                </>
              ) : (
                <Button
                  Icon={Share}
                  onClick={() => {
                    void shareLink();
                  }}
                  fullWidth
                >
                  Compartilhar link
                </Button>
              )}
            </div>
          </div>
          <FullScreenModalNominate
            isOpen={isNominateOpen}
            onClose={() => setIsNominateOpen(false)}
          />
          <CpsToast
            title="Link copiado para a área de transferência"
            show={openCopiedLinkToast}
            onClose={() => setOpenCopiedLinkToast(false)}
            type="success"
            duration={10}
          />
        </div>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/referral/")({
  component: ReferralPage,
});
